/**
 * ConfirmModal — a small reusable yes/no dialog. Body-level singleton-friendly.
 * `confirmModal.show({ title, message, confirmLabel, cancelLabel, danger })`
 * resolves true if the player confirms, false otherwise (cancel / backdrop tap).
 *
 * Styled to match ErrorModal / NotificationPrimer (neon-noir dark panel). The
 * confirm button can be styled `danger` (red) for destructive actions like
 * logging out.
 */

const STYLE_ID = 'sdl-confirm-styles';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions (default false → cyan). */
  danger?: boolean;
  /** Hide the cancel button → a single-button info dialog. */
  hideCancel?: boolean;
}

export class ConfirmModal {
  public element: HTMLElement;
  private titleEl: HTMLElement;
  private msgEl: HTMLElement;
  private confirmBtn: HTMLButtonElement;
  private cancelBtn: HTMLButtonElement;
  private visible = false;
  private resolver: ((ok: boolean) => void) | null = null;

  constructor() {
    ConfirmModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-confirm-host';
    this.element.innerHTML = `
      <div class="sdl-confirm-backdrop"></div>
      <div class="sdl-confirm-panel">
        <div class="sdl-confirm-title"></div>
        <div class="sdl-confirm-msg"></div>
        <button class="sdl-confirm-btn sdl-confirm-yes" data-yes data-sound="button"></button>
        <button class="sdl-confirm-btn sdl-confirm-no" data-no data-sound="button"></button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.titleEl = this.element.querySelector('.sdl-confirm-title') as HTMLElement;
    this.msgEl = this.element.querySelector('.sdl-confirm-msg') as HTMLElement;
    this.confirmBtn = this.element.querySelector('[data-yes]') as HTMLButtonElement;
    this.cancelBtn = this.element.querySelector('[data-no]') as HTMLButtonElement;

    this.confirmBtn.addEventListener('click', () => this.resolve(true));
    this.cancelBtn.addEventListener('click', () => this.resolve(false));
    this.element.querySelector('.sdl-confirm-backdrop')!.addEventListener('click', () => this.resolve(false));
  }

  show(opts: ConfirmOptions): Promise<boolean> {
    if (this.visible) return Promise.resolve(false);
    this.titleEl.textContent = opts.title;
    this.msgEl.textContent = opts.message;
    this.confirmBtn.textContent = opts.confirmLabel ?? 'CONFIRM';
    this.cancelBtn.textContent = opts.cancelLabel ?? 'CANCEL';
    this.cancelBtn.style.display = opts.hideCancel ? 'none' : '';
    this.confirmBtn.classList.toggle('sdl-confirm-danger', !!opts.danger);
    this.visible = true;
    this.element.classList.add('visible');
    return new Promise<boolean>((res) => { this.resolver = res; });
  }

  private resolve(ok: boolean): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    const r = this.resolver;
    this.resolver = null;
    r?.(ok);
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-confirm-host {
        position: fixed; inset: 0;
        z-index: 950;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-confirm-host.visible { display: flex; }
      .sdl-confirm-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75);
        backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);
      }
      .sdl-confirm-panel {
        position: relative;
        width: 300px;
        padding: 26px 22px 16px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(255,255,255,0.12);
        border-radius: 20px;
        display: flex; flex-direction: column; align-items: center;
        gap: 8px;
        box-shadow: 0 18px 50px rgba(0,0,0,0.6);
        animation: sdl-confirm-in 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes sdl-confirm-in {
        0% { opacity: 0; transform: scale(0.8); }
        100% { opacity: 1; transform: scale(1); }
      }
      .sdl-confirm-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 26px; letter-spacing: 1px; text-align: center;
        color: #fff;
      }
      .sdl-confirm-msg {
        font-family: 'Rajdhani', sans-serif; font-weight: 500;
        font-size: 14px; line-height: 1.45; text-align: center;
        color: rgba(255,255,255,0.72);
        margin-bottom: 10px;
      }
      .sdl-confirm-btn {
        width: 100%;
        padding: 13px 0;
        border: none; border-radius: 13px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px; letter-spacing: 2px;
        cursor: pointer;
        transition: transform 0.1s, background 0.15s, filter 0.15s;
      }
      .sdl-confirm-yes {
        background: linear-gradient(135deg, #00e5ff, #00a0c0);
        color: #04141a;
        margin-bottom: 4px;
      }
      .sdl-confirm-yes.sdl-confirm-danger {
        background: linear-gradient(135deg, #FF3366, #c01a44);
        color: #fff;
      }
      .sdl-confirm-yes:active { transform: scale(0.97); filter: brightness(1.1); }
      .sdl-confirm-no {
        background: rgba(255,255,255,0.08);
        color: rgba(255,255,255,0.66);
      }
      .sdl-confirm-no:active { transform: scale(0.97); background: rgba(255,255,255,0.14); }
    `;
    document.head.appendChild(style);
  }
}
